import React from 'react'

import type { UserRole } from '@bat/shared'
import { useAuth } from '@platform/auth/hooks/useAuth'
import { useTranslation } from '@platform/i18n/hooks/useTranslation'
import { Navigate, useLocation } from 'react-router-dom'

interface ProtectedRouteProps {
  children: React.ReactNode
  fallback?: string
  requiredRoles?: UserRole[]
}

/**
 * Role hierarchy levels
 * Higher number = more privileges
 */
const ROLE_HIERARCHY: Record<UserRole, number> = {
  super_admin: 4,
  org_admin: 3,
  user: 2,
  viewer: 1,
}

/**
 * The level of a role in the hierarchy. A custom role of the organisation is not in it: it gets the level of `user`
 * (above `viewer`, below `org_admin`), never more.
 */
function roleLevel(role: string): number {
  return Object.hasOwn(ROLE_HIERARCHY, role)
    ? ROLE_HIERARCHY[role as UserRole]
    : ROLE_HIERARCHY.user
}

/**
 * Check if user's role meets or exceeds the required role in hierarchy
 * Example: if route requires 'user', then 'org_admin' and 'super_admin' also have access
 */
function hasRequiredRole(userRole: string, requiredRoles: UserRole[]): boolean {
  const userLevel = roleLevel(userRole)

  // Check if user's role level meets or exceeds any of the required roles
  return requiredRoles.some((requiredRole) => userLevel >= ROLE_HIERARCHY[requiredRole])
}

/**
 * Determine redirect destination based on user's role
 */
function getRedirectForRole(userRole: string): string {
  switch (userRole) {
    case 'super_admin':
      return '/admin/system'
    case 'org_admin':
      return '/admin/org'
    default:
      return '/unauthorized'
  }
}

/**
 * Loading spinner shown during auth initialization
 */
const AuthLoadingSpinner: React.FC = () => {
  const { t } = useTranslation('auth')
  return (
    <div className="min-h-screen bg-crypto-dark flex items-center justify-center">
      <div className="flex flex-col items-center space-y-4">
        <div className="w-8 h-8 border-4 border-crypto-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-crypto-text text-sm">{t('protectedRoute.verifyingAuth')}</p>
      </div>
    </div>
  )
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  fallback = '/',
  requiredRoles,
}) => {
  const { isAuthenticated, user, isInitializing } = useAuth()
  const location = useLocation()

  // Show loading while auth is initializing
  // This prevents premature redirects during the auth check
  if (isInitializing) {
    return <AuthLoadingSpinner />
  }

  // User is not authenticated
  if (!isAuthenticated) {
    return <Navigate to={fallback} state={{ from: location }} replace />
  }

  // Check role-based authorization if roles are specified
  if (requiredRoles && user && requiredRoles.length > 0) {
    const userHasAccess = hasRequiredRole(user.role, requiredRoles)

    if (!userHasAccess) {
      // Redirect to appropriate page based on user's role
      const redirectTo = getRedirectForRole(user.role)
      return <Navigate to={redirectTo} replace />
    }
  }

  return <>{children}</>
}
